/**
 * HTTP layer for binary file attachments. Upload, list, patch, and delete of
 * guid-keyed binary files.
 *
 * Contains:
 * - fetchBinaryFiles: list attachments
 * - createBinaryFile / patchBinaryFile / deleteBinaryFile: attachment writes
 */
import apiClient from '@sophocles/http-common';

// Generic file storage endpoint.  See pliny.odl_app.views.binary_files.
// Detail / PATCH / DELETE use the file's `guid` as the path kwarg;
// list filters by entity_type / entity_id / file_type query params.
//
// Generic over (entity_type, file_type), so the same module backs:
// - customer/attachment  (replaces /customerattachments/)
// - customer/logo
// - customer/photo
// - site/photo           (replaces /sitephotos/)
// - sitegroup/photo
// - customuser/profile-photo
const ROOT = '/odl-app/binary-files';

export const fetchBinaryFiles = ({ entityType, entityId, fileType, ...rest } = {}) =>
  apiClient
    .get(`${ROOT}/`, {
      // Only set the named filters when provided, so callers can also pass
      // entity_id (or any other filter) through `...rest` — e.g. a site-scoped
      // list that additionally narrows to one camera — without an undefined
      // named arg clobbering it.
      params: {
        ...rest,
        ...(entityType !== undefined ? { entity_type: entityType } : {}),
        ...(entityId !== undefined ? { entity_id: entityId } : {}),
        ...(fileType !== undefined ? { file_type: fileType } : {}),
      },
    })
    .then(res => {
      const body = res.data;
      // Normalize the list response to the canonical odl-app envelope
      // { data: [...], meta: { total } }, whether the backend emits it
      // directly or an older DRF / payload shape, so all call sites consume
      // one shape.
      if (Array.isArray(body?.data)) return body;
      if (body?.payload?.results != null) {
        return { data: body.payload.results, meta: { total: body.payload.count ?? body.payload.results.length } };
      }
      if (Array.isArray(body?.results)) {
        return { data: body.results, meta: { total: body.count ?? body.results.length } };
      }
      if (Array.isArray(body)) return { data: body, meta: { total: body.length } };
      return body;
    });

// `payload` may be a FormData (file upload) or a plain object (no file change).
// FormData must include: entity_type, entity_id, file_type, file, comment?.
// BinaryFileViewSet composes RowWriteMixin (envelope="data"), so create/update
// echo the row as {"data": row} — unwrap with no fallback.
export const createBinaryFile = payload => {
  const isFormData = typeof FormData !== 'undefined' && payload instanceof FormData;
  return apiClient
    .post(`${ROOT}/`, payload, isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined)
    .then(res => res.data.data);
};

export const patchBinaryFile = (guid, payload) => {
  const isFormData = typeof FormData !== 'undefined' && payload instanceof FormData;
  return apiClient
    .patch(`${ROOT}/${guid}/`, payload, isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined)
    .then(res => res.data.data);
};

export const deleteBinaryFile = guid => apiClient.delete(`${ROOT}/${guid}/`);
